import { ChevronRight, LayoutCells, Plus } from "@gravity-ui/icons";
import { Button, Card } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { FormDialog } from "../components/dialogs";
import { SelectInput, TextInput } from "../components/fields";
import { EmptyBlock, ErrorBlock, LoadingBlock, Page, PageHeader, StateChip } from "../components/ui";
import { useFormat } from "../lib/format";
import { useApps, useCreateApp, useMe, useProfiles } from "../lib/queries";

export function AppsPage() {
  const { t } = useTranslation();
  const apps = useApps();
  const { data: me } = useMe();
  const fmt = useFormat();
  const [createOpen, setCreateOpen] = useState(false);
  const canEdit = me?.role !== "viewer";

  const newButton = canEdit ? (
    <Button onPress={() => setCreateOpen(true)}>
      <Plus />
      {t("apps.new")}
    </Button>
  ) : null;

  return (
    <Page>
      <PageHeader title={t("apps.title")} description={t("apps.subtitle")} actions={newButton} />
      {apps.isPending ? (
        <LoadingBlock />
      ) : apps.isError ? (
        <ErrorBlock error={apps.error} onRetry={() => apps.refetch()} />
      ) : apps.data.length === 0 ? (
        <Card>
          <EmptyBlock
            icon={LayoutCells}
            title={t("apps.empty")}
            hint={t("apps.emptyHint")}
            action={newButton}
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {apps.data.map((app) => (
            <Link
              key={app.id}
              to={`/apps/${app.id}`}
              className="group rounded-[28px] outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Card className="h-full gap-6 p-6 transition-shadow group-hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-base font-semibold">{app.name}</span>
                    <span className="truncate font-mono text-xs text-muted">{app.slug}</span>
                  </div>
                  <StateChip ok={app.status === "active"} label={t(`status.app.${app.status}`)} />
                </div>
                <div className="mt-auto flex items-center justify-between text-xs text-muted">
                  <span>
                    {t("common.created")} {fmt.relative(app.created_at)}
                  </span>
                  <ChevronRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
      <CreateAppDialog isOpen={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}

function CreateAppDialog({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const create = useCreateApp();
  const profiles = useProfiles();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [profileId, setProfileId] = useState<string | null>("new");

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("apps.createTitle")}
      submitLabel={t("common.create")}
      isPending={create.isPending}
      error={create.error}
      onSubmit={() =>
        create.mutate(
          {
            name,
            slug: slug || undefined,
            credential_profile_id: profileId && profileId !== "new" ? profileId : undefined,
          },
          {
            onSuccess: (app) => {
              onOpenChange(false);
              setName("");
              setSlug("");
              setProfileId("new");
              navigate(`/apps/${app.id}`);
            },
          },
        )
      }
    >
      <TextInput
        name="name"
        label={t("apps.name")}
        placeholder={t("apps.namePlaceholder")}
        value={name}
        onChange={setName}
        autoFocus
        isRequired
      />
      <TextInput
        name="slug"
        label={`${t("apps.slug")} (${t("common.optional").toLowerCase()})`}
        placeholder="aiyo-mobile"
        description={t("apps.slugHint")}
        value={slug}
        onChange={(v) => setSlug(v.toLowerCase())}
        mono
      />
      <SelectInput
        label={t("apps.profile")}
        description={t("apps.profileHint")}
        value={profileId}
        onChange={setProfileId}
        options={[
          { id: "new", label: t("apps.newProfile") },
          ...(profiles.data ?? []).map((p) => ({
            id: p.id,
            label: p.name,
            hint:
              p.channels.map((c) => `${t(`channels.${c.channel}`)}: ${c.aliases.join(", ")}`).join(" · ") ||
              undefined,
          })),
        ]}
      />
    </FormDialog>
  );
}
